import { useEffect, useRef, useState } from 'react';
import { UNIT_SYSTEMS, DEFAULT_UNIT_SYSTEM } from '../constants/unitSystems';
import { getProfile } from '../api/settings';
import { uploadFile } from '../api/repository';
import { classifyAttachment } from '../constants/attachments';
import PromptAttachments, { AttachButton } from '../components/PromptAttachments';
import FilePickerModal from '../components/FilePickerModal';
import AgentModeMenu from '../components/AgentModeMenu';
import { MODE_PLAN, nextMode, normalizeMode } from '../panels/chatModes';

function greeting(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// Just the first name: "Good morning, Joseph" is a greeting, the full name reads
// like a form field. displayName is user-typed and blank-when-unset (the backend
// never substitutes the email), so the first whitespace token is the name.
export function firstNameOf(displayName) {
  return (displayName || '').trim().split(/\s+/)[0] || null;
}

// What to call the user. A stated preferred name wins, because the token split
// guesses wrong on mononyms, multi-word given names and family-name-first
// ordering; blank falls back to it, so a profile that never set one is unchanged.
export function greetingNameOf(profile) {
  return (profile?.preferredName || '').trim() || firstNameOf(profile?.displayName);
}

/**
 * First non-empty line of the context, trimmed to something list-sized.
 *
 * With nothing typed, an attachment names the project — "Tutorial_Gear" reads
 * better in the rail than "Untitled project" for a submit that was entirely
 * "here is my part".
 */
export function deriveProjectName(context, attachments = []) {
  const first = (context || '')
    .split('\n')
    .map((s) => s.trim())
    .find(Boolean);
  if (first) return first.length > 60 ? `${first.slice(0, 57)}…` : first;
  const fileName = attachments.find((a) => a?.fileName)?.fileName;
  if (fileName) return fileName.replace(/\.[^.]+$/, '');
  return 'Untitled project';
}

/**
 * The dashboard's centre pane: a greeting and a composer that CREATES the
 * project.
 *
 * It deliberately does not reimplement project bootstrapping — see
 * `startConversation.js`, which creates the project and starts the SERVER agent
 * run without navigating. (`startProject.js`, the navigating handoff into
 * `ProjectPage`'s two gates, is now `/learn`'s alone.)
 *
 * Describe-your-problem ONLY: the composer always starts a blank canvas and the
 * assistant builds the workflow from the description. Picking a template,
 * running a guided tutorial and running one unattended on the server all moved
 * to `/learn`, which is where a walkthrough belongs — this pane used to ask four
 * setup questions before you could type a word.
 *
 * `threadActive` + `onSend` turn this into the FOLLOW-UP input of an existing
 * conversation (`DashboardThread`) rather than a project launcher. Additive: with
 * neither prop the component and its `onCreate` payload are exactly as before, so
 * the existing tests hold.
 *
 * In thread mode the greeting and the unit-system select are hidden. The greeting
 * because you are mid-conversation, not arriving; the unit system because it is
 * PERMANENT per project and already chosen — re-offering it would imply it can be
 * changed. Attachments keep working, so "here's the part I forgot" works as a
 * follow-up for free: the upload path is unchanged and the file lands in the same
 * repository the agent's `list_available_files` already reads.
 */
export default function DashboardComposer({
  onCreate,
  onSend,
  threadActive = false,
  disabled,
  // ── Permission mode ──
  // The project's sticky mode and the setter that persists a change. Omitting
  // `onSetAgentMode` ⇒ no selector renders. The `agentMode` fallback is PLAN, the
  // composer's own starting selection (`Dashboard.js` always passes it explicitly,
  // so this only serves a caller with no opinion) — deliberately NOT
  // `chatModes.DEFAULT_AGENT_MODE`, which is still `auto` and governs what a stored
  // or unknown mode reads as, not what a new conversation opens in.
  agentMode = MODE_PLAN,
  onSetAgentMode = null,
}) {
  const [context, setContext] = useState('');
  const [unitSystemId, setUnitSystemId] = useState(DEFAULT_UNIT_SYSTEM);
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);
  // Uploads start the moment a file is added, so a big STEP is already in S3 by
  // the time Send is pressed. `localId` (not fileId) keys the list because a chip
  // exists before the upload returns one.
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // The picker is owned HERE rather than by `Dashboard` (which owns every other
  // modal): its result lands in `attachments`, which this component owns, and
  // this component is mounted in both the bare and thread states — so both get
  // the feature without Dashboard threading a callback through two children.
  const [pickerOpen, setPickerOpen] = useState(false);
  const areaRef = useRef(null);
  const nextIdRef = useRef(0);

  useEffect(() => {
    let alive = true;
    getProfile()
      .then((p) => {
        if (alive) setName(greetingNameOf(p));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    areaRef.current?.focus();
  }, []);

  function patchAttachment(localId, patch) {
    setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, ...patch } : a)));
  }

  // Upload straight into the user's existing file repository (presigned PUT →
  // S3, not the backend relay: the relay only exists to return `s3Key` in one
  // round-trip, which we don't need, and it would push a large STEP through
  // CloudFront's 30 s and the ALB's 60 s timeouts).
  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const rejected = incoming.filter((f) => !classifyAttachment(f.name));
    // `accept` on the input is cosmetic and a drag-drop bypasses it entirely, so
    // this is the check that actually holds — and a rejected file is never
    // uploaded, rather than landing in the repository where nothing can read it.
    setAttachError(
      rejected.length
        ? `${rejected.map((f) => f.name).join(', ')} — only CAD (.stp/.step/.igs/.iges) and G-code (.gcode/.nc/.toolpath) files can be attached.`
        : null
    );
    for (const file of incoming) {
      const kind = classifyAttachment(file.name);
      if (!kind) continue;
      const localId = `a${nextIdRef.current++}`;
      setAttachments((prev) => [
        ...prev,
        {
          localId,
          fileName: file.name,
          fileSize: file.size,
          fileType: kind,
          status: 'uploading',
          progress: 0,
        },
      ]);
      uploadFile(file, (pct) => patchAttachment(localId, { progress: pct }))
        .then((fileId) => patchAttachment(localId, { fileId, status: 'ready', progress: 100 }))
        .catch((err) =>
          patchAttachment(localId, { status: 'error', error: err.message || String(err) })
        );
    }
  }

  // Attach a file that is ALREADY in the repository, by reference.
  //
  // The whole point is the call that ISN'T here: no `uploadFile`, so no
  // `POST /api/repository/upload-url`, so no second `file_{uuid}` row and no
  // second copy of the bytes for a file the account already has. Everything
  // downstream is identical — `submit` sends `{fileId, fileName, fileType}`
  // either way, so the agent cannot tell a picked file from an uploaded one.
  function addExisting(file) {
    // The picker already filters to what the composer accepts; this is the same
    // belt-and-braces as `addFiles`, since the repository holds `.npz` phase
    // fields and `.json` configs that no module could read as an attachment.
    const kind = classifyAttachment(file?.fileName);
    if (!kind) {
      setAttachError(
        `${file?.fileName} — only CAD (.stp/.step/.igs/.iges) and G-code (.gcode/.nc/.toolpath) files can be attached.`
      );
      return;
    }
    if (attachments.some((a) => a.fileId === file.fileId)) return;
    setAttachError(null);
    setAttachments((prev) => [
      ...prev,
      {
        localId: `a${nextIdRef.current++}`,
        fileId: file.fileId,
        fileName: file.fileName,
        fileSize: file.fileSize,
        fileType: kind,
        // Straight to ready: the bytes are already in S3, so there is nothing to
        // wait for and nothing for the Send gate below to hold.
        status: 'ready',
        progress: 100,
      },
    ]);
  }

  const uploading = attachments.some((a) => a.status === 'uploading');
  const ready = attachments.filter((a) => a.status === 'ready');
  // Nothing to act on: an empty description with nothing attached would create an
  // empty, unnamed project — which is what the old "+ New Project" button did.
  // An in-flight upload has no fileId yet, so it must not be sent.
  const canSubmit = !busy && !disabled && !uploading && (!!context.trim() || ready.length > 0);

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (threadActive) {
        // A follow-up message onto the live run. The backend QUEUES it rather than
        // starting a second run (its `/start` is deliberately not idempotent),
        // which is what makes "and also make the mesh finer" work mid-pipeline.
        await onSend(context.trim(), ready);
        setContext('');
        setAttachments([]);
        return;
      }
      // The three literals are the blank-canvas path, and `startProject` would
      // normalise them anyway — they are passed explicitly because
      // `handleProblemStatementConfirm` destructures all of them, so a reader
      // asking "what does the dashboard send for a template?" gets an answer here.
      await onCreate({
        name: deriveProjectName(context, ready),
        context: context.trim(),
        templateKey: null,
        unitSystemId,
        tutorial: false,
        autoMode: false,
        // The mode chosen BEFORE the project existed. There is no row to patch
        // yet, so it rides on the creation payload — `startConversation` writes it
        // to the row AND sends it on `/start`, so turn one is already gated.
        agentMode: normalizeMode(agentMode),
        // Only the repository refs travel — the File objects stay here. The
        // bytes are already in S3, so ProjectPage has nothing to upload.
        attachments: ready.map(({ fileId, fileName, fileType }) => ({
          fileId,
          fileName,
          fileType,
        })),
      });
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    // Shift+Tab cycles the permission mode. `preventDefault` is required: Tab
    // otherwise moves focus out of the composer, so the shortcut would both change
    // the mode and lose the caret. Checked BEFORE Enter because the two cannot
    // collide and this is the surprising one to a reader.
    //
    // Keeping the shortcut is what makes `chatModes.AGENT_MODES`' ORDER a real
    // contract with `agent_modes.AGENT_MODES` — with only the menu, order would be
    // presentation and the change-one-change-both note would be pointless.
    if (e.key === 'Tab' && e.shiftKey && onSetAgentMode) {
      e.preventDefault();
      onSetAgentMode(nextMode(agentMode));
      return;
    }
    // Enter sends, Shift+Enter newlines — matching ChatPanel's composer.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="dash-composer-pane">
      <div className="dash-composer-inner">
        {!threadActive && (
          <h1 className="dash-greeting">
            {/* The brand mark from the favicon, not a glyph — it is two colours, so
              no font character stands in for it. Decorative: the greeting text
              beside it carries the meaning, hence the empty alt. */}
            <img
              className="dash-greeting-mark"
              src={`${process.env.PUBLIC_URL}/HIDENN_favicon.png`}
              alt=""
              aria-hidden="true"
            />
            {greeting(new Date().getHours())}
            {name ? `, ${name}` : ''}
          </h1>
        )}

        <div
          className={`dash-composer${dragOver ? ' dragging' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer?.files);
          }}
        >
          <PromptAttachments
            files={attachments}
            onRemove={(localId) =>
              setAttachments((prev) => prev.filter((a) => a.localId !== localId))
            }
          />

          <textarea
            ref={areaRef}
            className="dash-composer-input"
            rows={3}
            placeholder={threadActive ? 'Reply to Dennis…' : 'What would you like to simulate?'}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
          />

          <div className="dash-composer-controls">
            <AttachButton onAdd={addFiles} onBrowse={() => setPickerOpen(true)} disabled={busy} />

            {/* Rendered in BOTH states, unlike the unit select below: the mode is
                not permanent, and picking Plan before the first message — "tell me
                what you'd do to this part" — is exactly what the mode is for. */}
            {onSetAgentMode && (
              <AgentModeMenu
                mode={agentMode}
                onSelect={onSetAgentMode}
                disabled={busy || disabled}
              />
            )}

            {/* The unit system is PERMANENT per project and was chosen when this
                one was created, so a follow-up must not appear to offer it. */}
            {!threadActive && (
              <select
                className="dash-composer-select"
                value={unitSystemId}
                onChange={(e) => setUnitSystemId(e.target.value)}
                disabled={busy}
                title="Unit system"
              >
                {Object.values(UNIT_SYSTEMS).map((sys) => (
                  <option key={sys.id} value={sys.id}>
                    {sys.label}
                  </option>
                ))}
              </select>
            )}

            <button
              className="dash-composer-send"
              onClick={submit}
              disabled={!canSubmit}
              title={threadActive ? 'Send' : 'Create project'}
              aria-label={threadActive ? 'Send' : 'Create project'}
            >
              {busy ? '…' : '↑'}
            </button>
          </div>
        </div>

        {attachError && (
          <p className="dash-attach-error" onClick={() => setAttachError(null)}>
            {attachError} <span>✕</span>
          </p>
        )}
      </div>

      <FilePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={addExisting}
        // So a file already on the prompt reads as "Added" rather than being
        // silently missing from the list.
        excludeFileIds={attachments.map((a) => a.fileId).filter(Boolean)}
      />
    </div>
  );
}
